import { DateTime } from 'luxon';

import { toZonedDateTime } from './zonedTime';

/**
 * Display text for an instant as a clock in a given IANA zone shows it.
 *
 * A separate module from `zonedTime` because formatting for a reader is presentation, and lint keeps
 * it out of a bounded context's `domain/` and `application/`, which can still use the conversions.
 */

const DATE_FIELDS = ['weekday', 'year', 'month', 'day', 'dateStyle'];
const TIME_FIELDS = ['dayPeriod', 'hour', 'minute', 'second', 'fractionalSecondDigits', 'timeStyle'];

// Mirrors `toLocaleDateString` / `toLocaleTimeString`: options naming none of the kind's own fields
// (e.g. only `timeZoneName`) still print the date or time rather than nothing.
const withDefaults = (options, ownFields, defaults) => {
  if (options && ownFields.every((field) => !options[field])) {
    return { ...defaults, ...options };
  }
  return options;
};

/**
 * @param {Date} instant
 * @param {string} zone - IANA zone
 * @param {{locales?: string, options?: Intl.DateTimeFormatOptions|null}} [format]
 * @returns {string}
 */
export function formatDateInZone(instant, zone, { locales = undefined, options = null } = {}) {
  const resolved = withDefaults(options, DATE_FIELDS, { year: 'numeric', month: 'numeric', day: 'numeric' });

  return toZonedDateTime(instant, zone).setLocale(locales).toLocaleString(resolved || DateTime.DATE_SHORT);
} // formatDateInZone()

/**
 * @param {Date} instant
 * @param {string} zone - IANA zone
 * @param {{locales?: string, options?: Intl.DateTimeFormatOptions|null}} [format]
 * @returns {string}
 */
export function formatTimeInZone(instant, zone, { locales = undefined, options = null } = {}) {
  const resolved = withDefaults(options, TIME_FIELDS, { hour: 'numeric', minute: 'numeric', second: 'numeric' });

  return toZonedDateTime(instant, zone).setLocale(locales).toLocaleString(resolved || DateTime.TIME_WITH_SECONDS);
} // formatTimeInZone()
